import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { formatBytes, formatDateTime, formatMoney, formatNumber } from '../ui/format';
import { ErrorState, Loading, Panel } from '../ui/Page';
import { percent, saasMetrics } from './api';

/**
 * Safe Cortex's own numbers.
 *
 * Each rate is shown with the figures it was computed from. A dashboard that says "retenção 85%" and
 * nothing else is one nobody can check, and an unverifiable number on a screen is a number people
 * stop believing after the first time it looks wrong.
 *
 * A dash rather than a zero wherever there is nothing to divide by. "No customers yet" and "no
 * revenue retained" are different facts, and showing 0% for the first is telling somebody their
 * business failed before it started.
 */

const PERIODS = [6, 12, 24];

export function MetricsPanel(): React.JSX.Element {
  const [months, setMonths] = useState(12);

  const metrics = useQuery({
    queryKey: ['saas-metrics', months],
    queryFn: () => saasMetrics(months),
  });

  if (metrics.isLoading) return <Loading />;
  if (metrics.error) {
    return <ErrorState error={metrics.error} onRetry={() => void metrics.refetch()} />;
  }

  const data = metrics.data!;
  const peak = Math.max(1, ...data.history.map((month) => month.invoicedCents));

  return (
    <div className="stack">
      <div className="cell-inline">
        {PERIODS.map((period) => (
          <button
            key={period}
            type="button"
            className={`chip${months === period ? ' chip--active' : ''}`}
            onClick={() => setMonths(period)}
          >
            {t('admin.lastMonths').replace('{n}', String(period))}
          </button>
        ))}

        <span className="muted small">{formatDateTime(data.asOf)}</span>
      </div>

      <div className="metrics">
        <Figure
          label={t('admin.mrr')}
          value={formatMoney(data.revenue.mrrCents)}
          hint={t('admin.payingAccounts').replace('{n}', formatNumber(data.revenue.payingAccounts))}
        />
        <Figure label={t('admin.arr')} value={formatMoney(data.revenue.arrCents)} />
        <Figure
          label={t('admin.arpu')}
          value={data.revenue.arpuCents === null ? '—' : formatMoney(data.revenue.arpuCents)}
        />
        <Figure
          label={t('admin.ltv')}
          value={data.lifetimeValueCents === null ? '—' : formatMoney(data.lifetimeValueCents)}
          hint={data.lifetimeValueCents === null ? t('admin.ltvUnknown') : undefined}
        />
      </div>

      <div className="home-grid">
        <Panel title={t('admin.retention')}>
          <div className="metrics">
            <Figure
              label={t('admin.grr')}
              value={percent(data.retention.grossRetention)}
              hint={t('admin.grrHint')}
            />
            <Figure
              label={t('admin.nrr')}
              value={percent(data.retention.netRetention)}
              hint={t('admin.nrrHint')}
            />
            <Figure label={t('admin.logoChurn')} value={percent(data.retention.logoChurn)} />
            <Figure label={t('admin.revenueChurn')} value={percent(data.retention.revenueChurn)} />
          </div>

          {/* The arithmetic, so somebody can check it. */}
          <p className="muted small">
            {t('admin.retentionBasis')
              .replace('{starting}', formatMoney(data.retention.startingCents))
              .replace('{accounts}', formatNumber(data.retention.startingAccounts))
              .replace('{churned}', formatMoney(data.retention.churnedCents))
              .replace('{lost}', formatNumber(data.retention.churnedAccounts))}
          </p>

          {/*
           * Null and zero are different answers, and the screen says which.
           *
           * Before the first monthly snapshot there is nothing to compare against, and a panel
           * showing "R$ 0,00 de expansão" would read as "nobody upgraded" rather than "we have
           * not measured a month yet".
           */}
          {data.retention.measuredFrom === null ? (
            <p className="muted small">{t('admin.retentionCaveat')}</p>
          ) : (
            <p className="muted small">
              {t('admin.movement')
                .replace('{expansion}', formatMoney(data.retention.expansionCents ?? 0))
                .replace('{contraction}', formatMoney(data.retention.contractionCents ?? 0))
                .replace('{new}', formatMoney(data.retention.newCents ?? 0))
                .replace('{month}', monthLabel(data.retention.measuredFrom))}
            </p>
          )}
        </Panel>

        <Panel title={t('admin.trials')}>
          <div className="metrics">
            <Figure
              label={t('admin.trialConversion')}
              value={percent(data.trials.conversion)}
              hint={t('admin.trialsBasis')
                .replace('{converted}', formatNumber(data.trials.converted))
                .replace('{ended}', formatNumber(data.trials.ended))}
            />
          </div>

          <h3 className="record__subhead">{t('admin.funnel')}</h3>
          <ul className="home-list">
            {data.funnel.map((entry) => (
              <li key={entry.status}>
                <span>{t(`admin.subscription.${entry.status}`)}</span>
                <span className="strong">{formatNumber(entry.total)}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <Panel title={t('admin.byMonth')}>
        {data.history.length === 0 ? (
          <p className="muted">{t('admin.noHistory')}</p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('admin.month')}</th>
                  <th className="numeric">{t('admin.invoiced')}</th>
                  <th className="numeric">{t('admin.collected')}</th>
                  <th />
                  <th className="numeric">{t('admin.newTenants')}</th>
                  <th className="numeric">{t('admin.churned')}</th>
                </tr>
              </thead>
              <tbody>
                {data.history.map((month) => (
                  <tr key={month.month}>
                    <td>{month.month}</td>
                    <td className="numeric">{formatMoney(month.invoicedCents)}</td>
                    <td className="numeric strong">{formatMoney(month.paidCents)}</td>
                    <td>
                      {/* Invoiced against collected, drawn: the gap is the collection story. */}
                      <span className="bars" aria-hidden="true">
                        <span
                          className="bars__issued"
                          style={{ width: `${(month.invoicedCents / peak) * 100}%` }}
                        />
                        <span
                          className="bars__paid"
                          style={{ width: `${(month.paidCents / peak) * 100}%` }}
                        />
                      </span>
                    </td>
                    <td className="numeric">{formatNumber(month.newTenants)}</td>
                    <td className="numeric">{formatNumber(month.churnedAccounts)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <div className="home-grid">
        <Panel title={t('admin.byPlan')}>
          {data.plans.length === 0 ? (
            <p className="muted">{t('admin.noPaidPlans')}</p>
          ) : (
            <ul className="home-list">
              {data.plans.map((plan) => (
                <li key={plan.key}>
                  <span className="cell-stack">
                    <span className="strong">{plan.name}</span>
                    <span className="muted small">
                      {t('admin.planAccounts').replace('{n}', formatNumber(plan.accounts))}
                    </span>
                  </span>
                  <span className="strong">{formatMoney(plan.mrrCents)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title={t('admin.platformUsage')}>
          <dl className="details">
            {Object.entries(data.usage).map(([key, value]) => (
              <div key={key} className="details__row">
                <dt>{t(`admin.usage.${key}`)}</dt>
                <dd>{key === 'storage_bytes' ? formatBytes(value) : formatNumber(value)}</dd>
              </div>
            ))}
          </dl>

          <p className="muted small">{t('admin.usageCaveat')}</p>
        </Panel>
      </div>
    </div>
  );
}

function Figure({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): React.JSX.Element {
  return (
    <div className="metric">
      <p className="metric__label">{label}</p>
      <p className="metric__value">{value}</p>
      {hint && <p className="muted small">{hint}</p>}
    </div>
  );
}

/** `2026-09-01` as `09/2026`: the month a movement describes, not a day anybody cares about. */
export function monthLabel(month: string): string {
  const [year, index] = month.split('-');

  return index && year ? `${index}/${year}` : month;
}
